import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeMaybe, takeEvery, race } from 'redux-saga/effects';
import { IRootState } from '.';
import SecondWindowService from 'src/service/SecondWindowService';

export interface ILayoutState {
    initialLoading: boolean;
    isGeneratingZip: boolean;
    isGeneratingInvoices: boolean;
    generating: boolean;
    activeDialog: string;
    globalSearchShowing: boolean;
    loadingPanels: Set<string>;
    secondWindowOpen: boolean;
    isSecondWindow: boolean;
    testingMode: boolean;
}

const initialState: ILayoutState = {
    initialLoading: true,
    generating: false,
    isGeneratingInvoices: false,
    isGeneratingZip: false,
    activeDialog: undefined,
    globalSearchShowing: false,
    loadingPanels: new Set<string>(),
    secondWindowOpen: false,
    isSecondWindow: false,
    testingMode: false,
};

// Action Types
export enum actionTypes {
    SET_GENERATING_ZIP = '[layout] SET_GENERATING_ZIP',
    SET_GENERATING_INVOICES = '[layout] SET_GENERATING_INVOICES',
    SET_GENERATING_DOCUMENTS = '[layout] SET_GENERATING_DOCUMENTS',
    SET_INITIAL_LOAD_STATE = '[layout] SET_INITIAL_LOAD_STATE',
    WAIT_INITIAL_LOAD_STATE = '[layout] WAIT_INITIAL_LOAD_STATE',
    SHOW_DIALOG = '[layout] SHOW_DIALOG',
    HIDE_DIALOG = '[layout] HIDE_DIALOG',
    SHOW_GLOBAL_SEARCH = '[layout] SHOW_GLOBAL_SEARCH',
    HIDE_GLOBAL_SEARCH = '[layout] HIDE_GLOBAL_SEARCH',
    SET_LOADING = '[layout] SET_LOADING',
    CLEAR_LOADING = '[layout] CLEAR_LOADING',
    OPEN_SECOND_WINDOW = '[layout] OPEN_SECOND_WINDOW',
    CLOSE_SECOND_WINDOW = '[layout] CLOSE_SECOND_WINDOW',
    SET_IS_SECOND_WINDOW = '[layout] SET_IS_SECOND_WINDOW',
    PUSH_TO_SECOND_WINDOW = '[layout] PUSH_TO_SECOND_WINDOW',
    SET_TESTING_MODE = '[layout] SET_TESTING_MODE',
}

// Action Creator
export const actionCreators = {
    setInitalLoadState: (setInitialLoad: boolean) => {
        return {
            type: actionTypes.SET_INITIAL_LOAD_STATE,
            payload: setInitialLoad,
        };
    },
    setGeneratingZip: (generating: boolean) =>
        ({ type: actionTypes.SET_GENERATING_ZIP, payload: generating }),
    setGeneratingInvoices: (generating: boolean) => {
        return {
            type: actionTypes.SET_GENERATING_INVOICES,
            payload: generating,
        };
    },
    setGeneratingDocuments: (generating: boolean) => {
        return {
            type: actionTypes.SET_GENERATING_DOCUMENTS,
            payload: generating,
        };
    },
    waitIniitalLoadState: () => {
        return { type: actionTypes.WAIT_INITIAL_LOAD_STATE };
    },
    showDialog: (dialogName: string) => {
        return { type: actionTypes.SHOW_DIALOG, payload: dialogName };
    },
    hideDialog: () => {
        return { type: actionTypes.HIDE_DIALOG };
    },
    showGlobalSearch: () => {
        return { type: actionTypes.SHOW_GLOBAL_SEARCH };
    },
    hideGlobalSearch: () => {
        return { type: actionTypes.HIDE_GLOBAL_SEARCH };
    },
    setLoading: (panelName: string) => {
        return { type: actionTypes.SET_LOADING, payload: panelName };
    },
    clearLoading: (panelName: string) => {
        return { type: actionTypes.CLEAR_LOADING, payload: panelName };
    },
    openSecondWindow: () => {
        return { type: actionTypes.OPEN_SECOND_WINDOW };
    },
    closeSecondWindow: () => {
        return { type: actionTypes.CLOSE_SECOND_WINDOW };
    },
    pushToSecondWindow: (path: string, search?: string) => {
        return {
            type: actionTypes.PUSH_TO_SECOND_WINDOW,
            payload: { path, search },
        };
    },
    setIsSecondWindow: () => {
        return { type: actionTypes.SET_IS_SECOND_WINDOW };
    },

    setTestingMode: () => {
        return { type: actionTypes.SET_TESTING_MODE };
    },
};

export const selectors = {
    isDialogShowing: (state: IRootState, dialogName: string): boolean =>
        state.layout.activeDialog !== undefined && state.layout.activeDialog.toLowerCase() === dialogName.toLowerCase(),
    isGlobalSearchShowing: (state: IRootState) => state.layout.globalSearchShowing,
    isPanelLoading: (state: IRootState, panelName: string): boolean => {
        return state.layout.loadingPanels.has(panelName);
    },
    isSecondWindow: (state: IRootState) => state.layout.isSecondWindow,
    isGeneratingZip: (state: IRootState) => state.layout.isGeneratingZip,
    isGeneratingInvoices: (state: IRootState) => state.layout.isGeneratingInvoices,
    isGeneratingDocuments: (state: IRootState) => state.layout.generating,
    secondWindowOpen: (state: IRootState) => state.layout.secondWindowOpen,
};

const reducer: Reducer<ILayoutState> = (state: ILayoutState = initialState, action: Action): ILayoutState => {
    if ((action.type as string).endsWith('_SUCCESS')) {
        const actionName = (action.type as string).replace('_SUCCESS', '');
        if (state.loadingPanels.has(actionName)) {
            state.loadingPanels.delete(actionName);
            return {
                ...state,
                loadingPanels: new Set(state.loadingPanels),
            };
        }
    }

    switch (action.type) {
        case actionTypes.SET_INITIAL_LOAD_STATE: {
            const payload = (action as { type: string; payload: boolean }).payload;
            return {
                ...state,
                initialLoading: payload,
            };
        }
        case actionTypes.SET_GENERATING_ZIP: {
            const payload = (action as { type: string; payload: boolean }).payload;
            return {
                ...state,
                isGeneratingZip: payload,
            };
        }
        case actionTypes.SET_GENERATING_INVOICES: {
            const payload = (action as { type: string; payload: boolean }).payload;
            return {
                ...state,
                isGeneratingInvoices: payload,
            };
        }
        case actionTypes.SET_GENERATING_DOCUMENTS: {
            const payload = (action as { type: string; payload: boolean }).payload;
            return {
                ...state,
                generating: payload,
            };
        }
        case actionTypes.SHOW_DIALOG: {
            const payload = (action as { type: string; payload: string }).payload;
            return {
                ...state,
                activeDialog: payload,
            };
        }
        case actionTypes.HIDE_DIALOG: {
            return {
                ...state,
                activeDialog: undefined,
            };
        }
        case actionTypes.SHOW_GLOBAL_SEARCH: {
            return {
                ...state,
                globalSearchShowing: true,
            };
        }
        case actionTypes.HIDE_GLOBAL_SEARCH: {
            return {
                ...state,
                globalSearchShowing: false,
            };
        }
        case actionTypes.SET_LOADING: {
            const panelName = (action as { type: string; payload: string }).payload;
            state.loadingPanels.add(panelName);
            const newState = {
                ...state,
                loadingPanels: new Set(state.loadingPanels),
            };
            return newState;
        }
        case actionTypes.OPEN_SECOND_WINDOW: {
            return {
                ...state,
                secondWindowOpen: true,
            };
        }
        case actionTypes.SET_IS_SECOND_WINDOW: {
            return {
                ...state,
                isSecondWindow: true,
            };
        }
        case actionTypes.SET_TESTING_MODE: {
            return {
                ...state,
                testingMode: true,
            };
        }
        default:
            return state;
    }
};

export default reducer;

const sagas = {
    waitInitialLoadState: function* (action: { type: string }): unknown {
        yield race({
            a1: takeMaybe(actionTypes.SET_INITIAL_LOAD_STATE),
            a2: delay(0),
        });
    },
    openSecondWindow: function* (action: { type: string }): unknown {
        const baseUrl = process.env.REACT_APP_BASE_URL;

        const x = screen.availWidth;
        const y = screen.availHeight;
        const h = 750;
        const w = x >= 1920 ? 1280 : 800;
        const left = x / 2 - w / 2;
        const top = y / 2 - h / 2;
        const windowOptions = `left=${left},top=${top},location=no,status=no,menubar=no,height=${h},width=${w}`;
        SecondWindowService.subscribe();
        yield call(window.open, `${baseUrl}/second-window?sw=true`, `${process.env.REACT_APP_TITLE} SuperView`, windowOptions);
    },
    pushToSecondWindow: function* (action: { type: string; payload: string }): unknown {
        yield call(SecondWindowService.sendMessage, 'LandHawk Superview', 'navigate', { payload: action.payload });
    },
    setIsSecondWindow: function* (action: { type: string }): unknown {
        yield call(SecondWindowService.subscribe);
    },
};

const watches = {
    watchInitialState: function* () {
        yield takeEvery(actionTypes.WAIT_INITIAL_LOAD_STATE, sagas.waitInitialLoadState);
    },
    watchOpenSecondWindow: function* () {
        yield takeEvery(actionTypes.OPEN_SECOND_WINDOW, sagas.openSecondWindow);
    },
    watchPushToSecondWindow: function* () {
        yield takeEvery(actionTypes.PUSH_TO_SECOND_WINDOW, sagas.pushToSecondWindow);
    },
    watchSetIsSecondWindow: function* () {
        yield takeEvery(actionTypes.SET_IS_SECOND_WINDOW, sagas.setIsSecondWindow);
    },
};

export function* saga() {
    yield fork(watches.watchInitialState);
    yield fork(watches.watchOpenSecondWindow);
    yield fork(watches.watchPushToSecondWindow);
    yield fork(watches.watchSetIsSecondWindow);
}



// WEBPACK FOOTER //
// ./src/reducers/Layout.ts